import { useEffect, useState, useMemo } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchCombobox, MultiSearchCombobox } from "@/components/ui/search-combobox";
import { toast } from "sonner";
import { Loader2, FileDown } from "lucide-react";
import {
  THIRD_PARTY_LABELS, STATUS_LABELS, SEVERITY_LABELS, CATEGORY_LABELS,
} from "./DisputesTab";
import DisputeHistoryList from "./DisputeHistoryList";
import DisputeExternalResponsibles from "./DisputeExternalResponsibles";
import DisputeHealthCard from "./DisputeHealthCard";
import { generateDisputePdf } from "@/lib/disputePdf";

const OUTCOMES = { amicable: "Accord amiable", abandoned: "Abandon", won: "Procès gagné", lost: "Procès perdu" };

const EMPTY = {
  third_party_type: "client",
  third_party_other_label: "",
  third_party_id: "",
  third_party_name: "",
  order_ids: [],
  category: "autre",
  reason: "",
  internal_owner_id: "",
  external_suppliers: [],
  external_thirdparties: [],
  status: "to_handle",
  severity: "medium",
  amount_at_stake: "",
  provision_made: false,
  next_action: "",
  next_action_due_date: "",
  closed_outcome: "",
  closing_comment: "",
  blacklisted: false,
};

// Types qui peuvent être rattachés à une fiche existante de la base.
const FICHE_TYPES = new Set(["client", "supplier", "subcontractor", "employee"]);
// Types saisis librement (pas de fiche dédiée). On propose un autocomplete depuis les litiges passés.
const FREE_TYPES = new Set(["interim_agency", "administration", "insurer", "banker", "other"]);

export const DisputeDialog = ({ open, onOpenChange, dispute, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]); // toutes les commandes (pour multi-select)
  const [pastDisputes, setPastDisputes] = useState([]); // historique pour autocomplete free-types
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Initialise le formulaire avec migration order_id → order_ids
    const initial = dispute
      ? {
          ...EMPTY,
          ...dispute,
          amount_at_stake: dispute.amount_at_stake ?? "",
          order_ids: (() => {
            const arr = Array.isArray(dispute.order_ids) ? [...dispute.order_ids] : [];
            if (dispute.order_id && !arr.includes(dispute.order_id)) arr.push(dispute.order_id);
            return arr;
          })(),
          external_suppliers: Array.isArray(dispute.external_suppliers) ? dispute.external_suppliers : [],
          external_thirdparties: Array.isArray(dispute.external_thirdparties) ? dispute.external_thirdparties : [],
        }
      : EMPTY;
    setForm(initial);

    (async () => {
      try {
        const [u, su, sub, em, cl, ord, dsp] = await Promise.all([
          api.getUsers?.().catch(() => ({ data: [] })) ?? Promise.resolve({ data: [] }),
          api.getSuppliers?.().catch(() => ({ data: [] })) ?? Promise.resolve({ data: [] }),
          api.getSubcontractors?.().catch(() => ({ data: [] })) ?? Promise.resolve({ data: [] }),
          api.getEmployees?.().catch(() => ({ data: [] })) ?? Promise.resolve({ data: [] }),
          api.getClients?.().catch(() => ({ data: { clients: [] } })) ?? Promise.resolve({ data: { clients: [] } }),
          api.getOrders?.().catch(() => ({ data: [] })) ?? Promise.resolve({ data: [] }),
          api.getDisputes?.({ include_closed: true }).catch(() => ({ data: [] })) ?? Promise.resolve({ data: [] }),
        ]);
        setUsers(u.data || []);
        setSuppliers(su.data || []);
        setSubcontractors(sub.data || []);
        setEmployees(em.data || []);
        const clientsList = Array.isArray(cl.data)
          ? cl.data
          : (cl.data?.items || cl.data?.clients || []);
        setClients(clientsList);
        setOrders(Array.isArray(ord.data) ? ord.data : []);
        setPastDisputes(Array.isArray(dsp.data) ? dsp.data : []);
      } catch (_e) { /* ignore */ }
    })();

    if (dispute?.id) {
      api.getDispute(dispute.id).then(({ data }) => setDetail(data)).catch(() => setDetail(null));
    } else {
      setDetail(null);
    }
  }, [open, dispute]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // ============ Tiers : selected item résolu depuis third_party_id ============
  const selectedThirdParty = useMemo(() => {
    if (!form.third_party_id) return null;
    if (form.third_party_type === "client") return clients.find(c => c.key === form.third_party_id) || null;
    if (form.third_party_type === "supplier") return suppliers.find(s => s.id === form.third_party_id) || null;
    if (form.third_party_type === "subcontractor") return subcontractors.find(s => s.id === form.third_party_id) || null;
    if (form.third_party_type === "employee") return employees.find(e => e.id === form.third_party_id) || null;
    return null;
  }, [form.third_party_id, form.third_party_type, clients, suppliers, subcontractors, employees]);

  const ficheList = useMemo(() => {
    if (form.third_party_type === "client") return clients;
    if (form.third_party_type === "supplier") return suppliers;
    if (form.third_party_type === "subcontractor") return subcontractors;
    if (form.third_party_type === "employee") return employees;
    return [];
  }, [form.third_party_type, clients, suppliers, subcontractors, employees]);

  const ficheGetKey = (item) => item.key || item.id;
  const ficheGetLabel = (item) => {
    if (form.third_party_type === "client") return item.client_name || "";
    if (form.third_party_type === "supplier") return item.company_name || item.name || "";
    if (form.third_party_type === "subcontractor") return item.company_name || `${item.first_name || ""} ${item.last_name || ""}`.trim();
    if (form.third_party_type === "employee") return `${item.first_name || ""} ${item.last_name || ""}`.trim();
    return "";
  };
  const ficheRenderItem = (item) => {
    if (form.third_party_type === "client") {
      const cityLabel = item.city || (Array.isArray(item.cities) ? item.cities.join(", ") : "");
      return (
        <>
          <span className="font-medium">{item.client_name}</span>
          {cityLabel && <span className="text-muted-foreground"> — {cityLabel}</span>}
        </>
      );
    }
    if (form.third_party_type === "supplier") {
      const cityLabel = item.city || "";
      return (
        <>
          <span className="font-medium">{item.company_name || item.name}</span>
          {cityLabel && <span className="text-muted-foreground"> — {cityLabel}</span>}
        </>
      );
    }
    if (form.third_party_type === "subcontractor") {
      const cn = item.company_name;
      const person = `${item.first_name || ""} ${item.last_name || ""}`.trim();
      return (
        <>
          <span className="font-medium">{cn || person || "Sous-traitant"}</span>
          {cn && person && <span className="text-muted-foreground"> — {person}</span>}
          {item.archived && (
            <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-400">Archivé</span>
          )}
        </>
      );
    }
    if (form.third_party_type === "employee") {
      return (
        <>
          <span className="font-medium">{item.first_name} {item.last_name}</span>
          {item.role && <span className="text-muted-foreground"> — {item.role}</span>}
          {item.exit_date && (
            <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-400">Sorti</span>
          )}
        </>
      );
    }
    return null;
  };
  const ficheFilter = (item, q) => {
    const label = ficheGetLabel(item).toLowerCase();
    const city = ((item.city || "") + " " + (Array.isArray(item.cities) ? item.cities.join(" ") : "")).toLowerCase();
    return label.includes(q) || city.includes(q);
  };

  const handleFicheSelect = (item) => {
    if (!item) {
      // Reset tiers + commandes liées (elles n'ont plus de sens si on change de tiers)
      setForm(prev => ({ ...prev, third_party_id: "", third_party_name: "", order_ids: [] }));
      return;
    }
    setForm(prev => ({
      ...prev,
      third_party_id: ficheGetKey(item),
      third_party_name: ficheGetLabel(item),
      order_ids: [], // reset orders quand on change de tiers
    }));
  };

  // Suggestions de noms pour free-types (depuis litiges passés du même type)
  const freeTypeSuggestions = useMemo(() => {
    if (!FREE_TYPES.has(form.third_party_type)) return [];
    const names = new Set();
    pastDisputes.forEach(d => {
      if (d.third_party_type === form.third_party_type && d.third_party_name) {
        names.add(d.third_party_name);
      }
    });
    return Array.from(names).sort().map(n => ({ id: n, name: n }));
  }, [pastDisputes, form.third_party_type]);

  // ============ Commandes ============
  // Par défaut on affiche TOUTES les commandes (laisser l'utilisateur chercher librement).
  // Si un client est sélectionné, un toggle permet de pré-filtrer sur les commandes
  // dont le `client_name` correspond exactement à celui du client choisi.
  const [filterOrdersByClient, setFilterOrdersByClient] = useState(false);
  const matchingOrdersCount = useMemo(() => {
    if (form.third_party_type !== "client" || !selectedThirdParty?.client_name) return 0;
    const target = selectedThirdParty.client_name.trim().toLowerCase();
    return orders.filter(o => (o.client_name || "").trim().toLowerCase() === target).length;
  }, [orders, form.third_party_type, selectedThirdParty]);
  const availableOrders = useMemo(() => {
    if (filterOrdersByClient && form.third_party_type === "client" && selectedThirdParty?.client_name) {
      const target = selectedThirdParty.client_name.trim().toLowerCase();
      return orders.filter(o => (o.client_name || "").trim().toLowerCase() === target);
    }
    return orders;
  }, [orders, filterOrdersByClient, form.third_party_type, selectedThirdParty]);

  const orderFilter = (o, q) => {
    const fields = [o.reference, o.client_name, o.city, o.order_type_name];
    return fields.some(f => (f || "").toLowerCase().includes(q));
  };
  const orderLabel = (o) => {
    const parts = [o.reference];
    if (o.client_name) parts.push(o.client_name);
    if (o.city) parts.push(o.city);
    return parts.filter(Boolean).join(" · ");
  };

  const handleSubmit = async () => {
    if (!form.third_party_name.trim()) { toast.error("Nom du tiers requis"); return; }
    if (!form.reason.trim()) { toast.error("Motif requis"); return; }
    if (form.third_party_type === "other" && !form.third_party_other_label.trim()) {
      toast.error("Précisez le type 'Autre'"); return;
    }
    const payload = {
      ...form,
      amount_at_stake: form.amount_at_stake === "" ? null : parseFloat(form.amount_at_stake),
      next_action_due_date: form.next_action_due_date || null,
      third_party_id: form.third_party_id || null,
      order_ids: form.order_ids || [],
      internal_owner_id: form.internal_owner_id || null,
      external_suppliers: (form.external_suppliers || [])
        .filter((s) => s.supplier_id)
        .map((s) => ({
          supplier_id: s.supplier_id,
          role: (s.role || "").trim() || null,
          comment: (s.comment || "").trim() || null,
        })),
      external_thirdparties: (form.external_thirdparties || [])
        .filter((t) => (t.name || t.company || t.email || t.phone))
        .map((t) => ({
          id: t.id || null,
          company: (t.company || "").trim() || null,
          name: (t.name || "").trim() || null,
          phone: (t.phone || "").trim() || null,
          email: (t.email || "").trim() || null,
          role: (t.role || "").trim() || null,
          comment: (t.comment || "").trim() || null,
        })),
      closed_outcome: form.closed_outcome || null,
      closing_comment: form.closing_comment || null,
      next_action: form.next_action || null,
      third_party_other_label: form.third_party_other_label || null,
    };
    // On retire le legacy single order_id côté front — le backend reconstruira pour la compat.
    delete payload.order_id;

    try {
      setSaving(true);
      if (dispute?.id) await api.updateDispute(dispute.id, payload);
      else await api.createDispute(payload);
      toast.success(dispute?.id ? "Litige mis à jour" : "Litige créé");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    } finally {
      setSaving(false);
    }
  };

  const isFiche = FICHE_TYPES.has(form.third_party_type);
  const isFree = FREE_TYPES.has(form.third_party_type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dispute-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {dispute?.id ? "Modifier le litige" : "Nouveau litige"}
          </DialogTitle>
          <DialogDescription>
            Saisis les informations du dossier. Un email est envoyé au responsable interne à la création.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Type de tiers */}
          <div className="space-y-1">
            <Label>Type de tiers *</Label>
            <Select value={form.third_party_type} onValueChange={(v) => setForm(prev => ({
              ...prev, third_party_type: v, third_party_id: "", third_party_name: "", order_ids: [],
            }))}>
              <SelectTrigger data-testid="dispute-third-party-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(THIRD_PARTY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.third_party_type === "other" && (
            <div className="space-y-1">
              <Label>Préciser *</Label>
              <Input
                value={form.third_party_other_label}
                onChange={(e) => update("third_party_other_label", e.target.value)}
                data-testid="dispute-other-label"
                placeholder="Ex : Voisin, Mairie, …"
              />
            </div>
          )}

          {/* Sélection du tiers (search) — types liés à une fiche */}
          {isFiche && (
            <div className="space-y-1 col-span-2">
              <Label>Rechercher dans les fiches</Label>
              <SearchCombobox
                items={ficheList}
                filterFn={ficheFilter}
                renderItem={ficheRenderItem}
                getKey={ficheGetKey}
                getLabel={ficheGetLabel}
                selected={selectedThirdParty}
                onSelect={handleFicheSelect}
                placeholder={`Tapez un nom de ${THIRD_PARTY_LABELS[form.third_party_type].toLowerCase()}…`}
                testId="dispute-third-party-search"
              />
              <p className="text-xs text-muted-foreground">
                Sélectionne la fiche concernée. Le nom sera reporté automatiquement et un badge ⚠️ apparaîtra sur sa fiche.
              </p>
            </div>
          )}

          {/* Free-types : champ libre + suggestions */}
          {isFree && (
            <div className="space-y-1 col-span-2">
              <Label>Nom du tiers *</Label>
              <SearchCombobox
                items={freeTypeSuggestions}
                filterFn={(it, q) => it.name.toLowerCase().includes(q)}
                renderItem={(it) => <span>{it.name}</span>}
                getKey={(it) => it.id}
                getLabel={(it) => it.name}
                selected={form.third_party_name ? { id: form.third_party_name, name: form.third_party_name } : null}
                onSelect={(it) => update("third_party_name", it?.name || "")}
                placeholder={`Tapez le nom de ${THIRD_PARTY_LABELS[form.third_party_type].toLowerCase()}…`}
                testId="dispute-third-party-free"
              />
              {!form.third_party_name && (
                <Input
                  className="mt-1"
                  placeholder="Ou saisissez librement un nouveau nom…"
                  value={form.third_party_name}
                  onChange={(e) => update("third_party_name", e.target.value)}
                  data-testid="dispute-third-party-name"
                />
              )}
              <p className="text-xs text-muted-foreground">
                Pas de fiche dédiée pour ce type — vous pouvez saisir librement ou choisir un nom déjà utilisé.
              </p>
            </div>
          )}

          {/* Nom affiché (ajustable si fiche sélectionnée) */}
          {isFiche && (
            <div className="space-y-1 col-span-2">
              <Label>Nom affiché *</Label>
              <Input
                value={form.third_party_name}
                onChange={(e) => update("third_party_name", e.target.value)}
                data-testid="dispute-third-party-name"
                placeholder="Sera prérempli depuis la fiche…"
              />
            </div>
          )}

          {/* Rattachement commandes — multi-select avec toggle de pré-filtrage */}
          <div className="space-y-1 col-span-2">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <Label>Commandes liées (optionnel)</Label>
              {form.third_party_type === "client" && selectedThirdParty && (
                <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={filterOrdersByClient}
                    onChange={(e) => setFilterOrdersByClient(e.target.checked)}
                    className="cursor-pointer"
                    data-testid="dispute-orders-filter-by-client"
                  />
                  <span>
                    Filtrer sur ce client{" "}
                    <span className="text-zinc-400">({matchingOrdersCount} cmd)</span>
                  </span>
                </label>
              )}
            </div>
            <MultiSearchCombobox
              items={availableOrders}
              filterFn={orderFilter}
              renderItem={(o) => (
                <>
                  <span className="font-mono text-xs text-zinc-500 mr-2">{o.reference}</span>
                  <span className="font-medium">{o.client_name}</span>
                  {o.city && <span className="text-muted-foreground"> — {o.city}</span>}
                  {o.order_type_name && <span className="text-indigo-700"> · {o.order_type_name}</span>}
                </>
              )}
              getKey={(o) => o.id}
              getLabel={orderLabel}
              selectedKeys={form.order_ids}
              onChange={(ids) => update("order_ids", ids)}
              placeholder="Rechercher une commande à rattacher (référence, client, ville…)"
              testId="dispute-orders-search"
              emptyHint="Aucune commande ne correspond"
            />
            <p className="text-xs text-muted-foreground">
              Un indicateur ⚠️ apparaîtra sur chaque commande rattachée. Vous pouvez en ajouter plusieurs.
            </p>
          </div>

          <div className="space-y-1">
            <Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger data-testid="dispute-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Responsable interne</Label>
            <Select value={form.internal_owner_id || "_none"} onValueChange={(v) => update("internal_owner_id", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="dispute-internal-owner"><SelectValue placeholder="Non assigné" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Non assigné</SelectItem>
                {(() => {
                  // Filtre strict : uniquement les users de l'org actuelle, liés à un employé actif (sans exit_date)
                  const activeEmpIds = new Set(
                    employees.filter((e) => !e.exit_date && !e.archived).map((e) => e.id)
                  );
                  const filtered = users.filter((u) => u.employee_id && activeEmpIds.has(u.employee_id));
                  // Fallback : si aucun user ne matche (org sans liaison user↔employee), on affiche tous les users de l'org renvoyés par le backend (déjà filtrés par owner_id).
                  const list = filtered.length > 0 ? filtered : users;
                  return list.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1 col-span-2">
            <Label>Motif du litige *</Label>
            <Textarea rows={3} value={form.reason} onChange={(e) => update("reason", e.target.value)} data-testid="dispute-reason" />
          </div>

          {/* Responsables externes (fournisseurs + tiers) */}
          <div className="col-span-2">
            <DisputeExternalResponsibles
              value={form}
              onChange={setForm}
              suppliers={suppliers}
            />
          </div>

          <div className="space-y-1">
            <Label>Statut</Label>
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger data-testid="dispute-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Gravité</Label>
            <Select value={form.severity} onValueChange={(v) => update("severity", v)}>
              <SelectTrigger data-testid="dispute-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SEVERITY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Enjeu financier (€)</Label>
            <Input type="number" step="0.01" value={form.amount_at_stake} onChange={(e) => update("amount_at_stake", e.target.value)} data-testid="dispute-amount" />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <Checkbox id="provision" checked={form.provision_made} onCheckedChange={(v) => update("provision_made", !!v)} data-testid="dispute-provision" />
            <Label htmlFor="provision" className="cursor-pointer">Provision comptable effectuée</Label>
          </div>

          <div className="space-y-1">
            <Label>Prochaine action</Label>
            <Input value={form.next_action} onChange={(e) => update("next_action", e.target.value)} placeholder="Ex : Appeler avocat" data-testid="dispute-next-action" />
          </div>
          <div className="space-y-1">
            <Label>Date d&apos;échéance</Label>
            <Input type="date" value={form.next_action_due_date} onChange={(e) => update("next_action_due_date", e.target.value)} data-testid="dispute-due-date" />
          </div>

          {form.status === "closed" && (
            <>
              <div className="space-y-1 col-span-2 pt-2 border-t">
                <Label>Issue du litige</Label>
                <Select value={form.closed_outcome || "_none"} onValueChange={(v) => update("closed_outcome", v === "_none" ? "" : v)}>
                  <SelectTrigger data-testid="dispute-outcome"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {Object.entries(OUTCOMES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Commentaire de clôture</Label>
                <Textarea rows={2} value={form.closing_comment} onChange={(e) => update("closing_comment", e.target.value)} data-testid="dispute-closing-comment" />
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <Checkbox id="blacklist" checked={form.blacklisted} onCheckedChange={(v) => update("blacklisted", !!v)} data-testid="dispute-blacklist" />
                <Label htmlFor="blacklist" className="cursor-pointer text-red-700 font-semibold">Blacklister ce tiers</Label>
              </div>
            </>
          )}
        </div>

        {/* History section (only on edit) */}
        {dispute?.id && (
          <div className="pt-4 border-t space-y-3">
            <DisputeHealthCard dispute={detail || dispute} history={detail?.history || []} />
            <DisputeHistoryList
              disputeId={dispute.id}
              entries={detail?.history || []}
              dispute={detail || dispute}
              users={users}
              suppliers={suppliers}
              onChanged={async () => {
                try {
                  const { data } = await api.getDispute(dispute.id);
                  setDetail(data);
                } catch (_e) { /* ignore */ }
              }}
            />
          </div>
        )}

        <DialogFooter>
          {dispute?.id && (
            <Button variant="outline" onClick={() => generateDisputePdf(detail || dispute)} data-testid="dispute-export-pdf">
              <FileDown className="h-4 w-4 mr-1" /> Exporter en PDF
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="dispute-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {dispute?.id ? "Enregistrer" : "Créer le litige"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DisputeDialog;
